/**
 * A seat's outbound caller ID, and the "awaiting a caller ID" state.
 *
 * SD-19310 · Unassigning a Telnyx port order clears the moved number from the
 * seats that used it as their caller ID (SD-19304). The seat and its user
 * remain, so every surface has to render and operate a seat that has no caller
 * ID yet instead of assuming one is always present.
 *
 * The wire representation is normalized HERE, once. dale's `outboundIdentity`
 * is a required non-null string today (`phone_seat.py`), and the cleared form
 * may land as null, an empty string, or an absent key depending on how the
 * backend models it. Reading this helper instead of the raw field is what lets
 * the web-app deploy ahead of that change and need no rework whichever form is
 * chosen.
 *
 * Deliberately a leaf module with no imports: the dock runtime and
 * `callability` both need it, and neither may pull the seat-form graph in.
 */

/** The seat fields this module reads — structural so a caller can pass a seat,
 *  a roster row, or a line-less own-seat snapshot without a cast. */
export type SeatCallerIdentity = {
  outboundIdentity?: string | null;
};

/**
 * The seat's E.164 caller ID, or null when it is awaiting one.
 *
 * Trims before the emptiness test: a whitespace-only value is not a caller ID
 * and must never be presented or dialed as one.
 */
export const seatCallerId = (
  seat: SeatCallerIdentity | null | undefined,
): string | null => {
  const identity = seat?.outboundIdentity;
  // The truthiness test covers null, an absent key and the legacy empty
  // string; `trim` only adds whitespace-only. The stored value is returned
  // unaltered — trimming is for the emptiness decision, not the number.
  return identity && identity.trim() !== "" ? identity : null;
};

/**
 * A seat that exists but has no caller ID, so it cannot place outbound calls
 * until an admin selects an eligible replacement.
 *
 * An absent seat is NOT awaiting a caller ID — there is no seat to configure,
 * which is the separate unseated state `phoneSeatEmptyState` covers.
 */
export const isAwaitingCallerId = (
  seat: SeatCallerIdentity | null | undefined,
): boolean => !!seat && seatCallerId(seat) === null;

/** A seat's line as dale's `/seats/me/lines` lists it — only the default flag
 *  matters here, so the app dock and the extension can share one rule. */
type ListedLine = { isDefault: boolean };

/**
 * Whether a loaded line list offers the seat no caller ID of its own.
 *
 * SD-20233 · dale lists the seat's own caller ID as its default line only
 * while the seat may call from it. A seat awaiting a caller ID has no default,
 * and neither does one whose number was retired after a port-out — its list
 * holds only shared lines, or none. dale refuses every call it places from
 * such a seat, a shared line included, until an admin chooses a new caller ID,
 * and the departed number has no registered emergency address. Calling
 * surfaces treat it exactly like a seat awaiting one, 911 included.
 *
 * Null or undefined (not loaded yet, a failed read, a backend predating the
 * list) is not evidence of anything and answers false.
 */
export const lacksCallableDefaultLine = (
  lines: readonly ListedLine[] | null | undefined,
): boolean => lines != null && !lines.some((line) => line.isDefault);

/**
 * Whether two seats present the same caller ID.
 *
 * Seats awaiting a caller ID never match — not each other, and not a configured
 * seat. Comparing the raw fields would make two cleared seats look like they
 * share a line, because both `null === null` and `"" === ""` hold; that would
 * let one unconfigured seat adopt another's 911 registration and render as
 * "911 ready" with no number at all.
 */
export const sharesCallerId = (
  seat: SeatCallerIdentity | null | undefined,
  other: SeatCallerIdentity | null | undefined,
): boolean => {
  const identity = seatCallerId(seat);
  return identity !== null && identity === seatCallerId(other);
};

/**
 * dale's machine-readable refusal when origination is attempted from a seat
 * awaiting a caller ID: `409 {"detail": {"code": "seat_awaiting_caller_id"}}`
 * (SD-19304).
 *
 * Matched on the CODE, never the status or the prose. A `400 "Outbound
 * identity must be a phone number this account owns"` is a DIFFERENT fault — a
 * configured seat pointing at a number its account no longer holds — and must
 * keep its own message rather than being relabeled as awaiting.
 *
 * The dock blocks these dials locally, so this is the backstop for a tab that
 * loaded before the seat's number was unassigned.
 */
export const SEAT_AWAITING_CALLER_ID_CODE = "seat_awaiting_caller_id";

/**
 * dale's code for a seat whose caller ID left the account through a completed
 * port-out. It arrives on a refused outbound call, on a refused desk-phone
 * setup, and as the seat's persisted `emergencyLastError`.
 */
export const CALLER_ID_PORTED_OUT_CODE = "caller_id_ported_out";

/** Short label for the prompt shown wherever a caller-ID-less seat appears. */
export const AWAITING_CALLER_ID_LABEL = "Choose a caller ID";

/** Last-resort label for a seat whose user the roster didn't resolve and whose
 *  caller ID was cleared. Both fallbacks gone would otherwise render a blank,
 *  unidentifiable option in the transfer picker, the route editor, and the
 *  queue member list. */
export const UNNAMED_SEAT_LABEL = "Unnamed seat";

/**
 * Why an outbound call can't be placed from a seat awaiting a caller ID.
 *
 * Static app copy, never the server's refusal text — dale's own message for
 * this case can't distinguish a cleared caller ID from a number the account
 * doesn't own, and the repo rule is static copy for error states regardless.
 *
 * One string for every calling surface, deliberately. The seat holder may be
 * the admin who can fix it or a member who can't, and the dock has no reason
 * to know which, so it states the fact and who acts instead of branching on
 * role — two near-identical strings is how this copy drifts.
 */
export const AWAITING_CALLER_ID_CALL_REASON =
  "Your seat has no outbound caller ID yet, so calls can't be placed. An admin can choose one on the Seats page.";

/** Why a call must be retried after its configured caller number changed. */
export const CALLER_ID_CHANGED_DURING_CALL_START_REASON =
  "Your caller ID changed while the call was starting. Try the call again.";

/**
 * Why 911 doesn't work from a seat awaiting a caller ID: such a seat places no
 * calls at all, 911 included, and its emergency address is registered against
 * the caller ID it is given. It must not read as an address problem, which is
 * what the other not-routing copy describes.
 */
export const AWAITING_CALLER_ID_E911_MESSAGE =
  "This seat has no outbound caller ID, so its calls, including 911, won't go through. Choose a caller ID and save the seat to turn calling back on.";

/**
 * Shown to the seat holder when they dial 911 or another N11 code from a seat
 * awaiting a caller ID. Kari's Law: a dead Call button must never be the only
 * signal, so it names the one thing that actually works right now — another
 * phone. Distinct from the not-connected wording, because this phone IS
 * connected and waiting will not fix it.
 */
/**
 * The desk-phone 911 alarm on a seat awaiting a caller ID: its desk phone
 * places no calls, 911 included. The standard desk-phone copy ends "Set the
 * seat's E911 address and re-save to register it", which is a dead end here:
 * the address is registered against the caller ID, so one has to be chosen
 * first.
 */
export const AWAITING_CALLER_ID_DESK_PHONE_MESSAGE =
  "This seat has no outbound caller ID, so its desk phone can't place calls, including 911. Choose this seat's caller ID and save the seat to turn calling back on and register its emergency address.";

export const AWAITING_CALLER_ID_EMERGENCY_MESSAGE =
  "This seat has no outbound caller ID, so an emergency call won't go through here and wouldn't carry your location. To reach emergency services, call from another phone.";

/**
 * Why an outbound call can't be placed from a seat whose caller ID was ported
 * out. The seat still carries the departed number, so the awaiting-caller-ID
 * wording ("no outbound caller ID yet") would describe the wrong problem. Says
 * "number", matching the Seats page's "Number ported out" / "Choose new number"
 * that the admin who fixes it will look for.
 */
export const CALLER_ID_PORTED_OUT_CALL_REASON =
  "This seat's number moved to another carrier, so no calls can be placed from here, 911 included. An admin can choose a new number on the Seats page.";

/** Shown when 911 or another N11 code is dialed from a seat whose caller ID was
 *  ported out. Leads with another phone, as Kari's Law requires of a blocked
 *  dial, and makes no claim about where a call from the departed number would
 *  go. */
export const CALLER_ID_PORTED_OUT_EMERGENCY_MESSAGE =
  "To reach emergency services, call from another phone. This seat's number moved to another carrier, so emergency calls can't be placed here.";

/**
 * Why a seat can't place ordinary calls: it has no caller ID yet, or the one
 * it carries was ported out. Both are fixed by an admin choosing a caller ID.
 * Awaiting a caller ID also blocks emergency calls. A ported-out caller ID
 * blocks an emergency call only when the call would present it; one that
 * presents another of the seat's own numbers with confirmed E911 goes out.
 */
export type CallerIdBlock = "awaitingCallerId" | "callerIdPortedOut";

/** What a dialer shows for one blocked state. */
export type CallerIdBlockCopy = {
  title: string;
  callReason: string;
  emergencyMessage: string;
};

/** The copy each blocked state shows. Dialers render it directly; refusals
 *  from other entry points take it through `callerIdRefusal`. */
export const CALLER_ID_BLOCK_COPY = {
  awaitingCallerId: {
    title: "No outbound caller ID",
    callReason: AWAITING_CALLER_ID_CALL_REASON,
    emergencyMessage: AWAITING_CALLER_ID_EMERGENCY_MESSAGE,
  },
  callerIdPortedOut: {
    title: "Number ported out",
    callReason: CALLER_ID_PORTED_OUT_CALL_REASON,
    emergencyMessage: CALLER_ID_PORTED_OUT_EMERGENCY_MESSAGE,
  },
} satisfies Record<CallerIdBlock, CallerIdBlockCopy>;

/**
 * Which blocked state a seat is in, or null when it can call.
 *
 * `awaiting` wins: a seat with no caller ID has no default line either, and its
 * copy is the accurate one. A missing default line only means "ported out" for
 * a seat that does carry a caller ID.
 */
export const callerIdBlockFor = ({
  awaiting,
  lacksDefaultLine,
}: {
  awaiting: boolean;
  lacksDefaultLine: boolean;
}): CallerIdBlock | null =>
  awaiting ? "awaitingCallerId" : lacksDefaultLine ? "callerIdPortedOut" : null;

/**
 * The message a refused dial shows for a blocked seat: the emergency message
 * for an emergency code, so it names another phone, and the call reason
 * otherwise. The one place that choice is made, for every refusing surface.
 */
export const callerIdRefusal = (
  block: CallerIdBlock,
  emergency: boolean,
): string =>
  emergency
    ? CALLER_ID_BLOCK_COPY[block].emergencyMessage
    : CALLER_ID_BLOCK_COPY[block].callReason;

/** Stops an initial send locally, or a replay after its prior HTTP attempt. */
export class CallerIdentityUnavailableError extends Error {
  constructor() {
    super(AWAITING_CALLER_ID_CALL_REASON);
    this.name = "CallerIdentityUnavailableError";
  }
}
